import { useState } from 'react';
import {
  Alert,
  Anchor,
  Button,
  Checkbox,
  Container,
  Group,
  Paper,
  PasswordInput,
  SegmentedControl,
  Stack,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { Link, useNavigate } from 'react-router-dom';
import {
  clientTypeSchema,
  registerClientSchema,
  type ClientType,
  type RegisterClientRequest,
} from '@expeditor/shared';
import { authApi } from '../../shared/api/auth';
import { useAuthStore } from '../../shared/auth/auth.store';
import { homeRouteForRole } from '../../shared/auth/home-route';
import { CompanyLookup } from '../../shared/ui/CompanyLookup';
import { DateField } from '../../shared/ui/DateField';
import type { CompanySuggestion } from '@expeditor/shared';

const TYPE_LABELS: Record<ClientType, string> = {
  INDIVIDUAL: 'Физлицо',
  ENTREPRENEUR: 'ИП',
  LEGAL_RU: 'ЮЛ РФ',
  LEGAL_FOREIGN: 'Иностранная орг.',
};

/**
 * Страница регистрации клиента.
 *
 * Type-specific блок переключается через SegmentedControl. Валидация выполняется
 * через Zod-схему регистрации из @expeditor/shared — она же используется на сервере.
 *
 * После успешной регистрации:
 * 1. Показываем баннер с напоминанием подтвердить email.
 * 2. На dev-сервере (если backend вернул devVerification) — даём кнопку «перейти
 *    по ссылке подтверждения» и кнопку «открыть лог-сообщение».
 * 3. После подтверждения предлагаем войти.
 */
/**
 * mode:
 *  - 'self' (по умолчанию) — публичная само-регистрация клиента.
 *  - 'demo-client' — демо-сотрудник (MANAGER) заводит своего демо-клиента
 *    (виден только ему). Токены текущего пользователя НЕ трогаем; после успеха
 *    показываем логин созданного клиента, чтобы войти под ним в другом окне.
 */
export function RegisterPage({ mode = 'self' }: { mode?: 'self' | 'demo-client' } = {}) {
  const nav = useNavigate();
  const setTokens = useAuthStore((s) => s.setTokens);
  const setUser = useAuthStore((s) => s.setUser);
  const isDemoClient = mode === 'demo-client';

  const [type, setType] = useState<ClientType>('LEGAL_RU');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [phone, setPhone] = useState('');

  // ФЛ
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [middleName, setMiddleName] = useState('');
  const [passportSerial, setPassportSerial] = useState('');
  const [passportNumber, setPassportNumber] = useState('');
  const [passportIssueDate, setPassportIssueDate] = useState('');
  const [passportIssuedBy, setPassportIssuedBy] = useState('');
  const [individualRegAddress, setIndividualRegAddress] = useState('');
  const [actualAddress, setActualAddress] = useState('');

  // ИП
  const [innIp, setInnIp] = useState('');
  const [ogrnip, setOgrnip] = useState('');
  const [ipRegAddress, setIpRegAddress] = useState('');

  // ЮЛ РФ
  const [legalFullName, setLegalFullName] = useState('');
  const [legalShortName, setLegalShortName] = useState('');
  const [legalInn, setLegalInn] = useState('');
  const [legalKpp, setLegalKpp] = useState('');
  const [legalOgrn, setLegalOgrn] = useState('');
  const [legalAddress, setLegalAddress] = useState('');
  const [directorName, setDirectorName] = useState('');

  // Иностранная
  const [foreignFullName, setForeignFullName] = useState('');
  const [foreignShortName, setForeignShortName] = useState('');
  const [country, setCountry] = useState('');
  const [taxId, setTaxId] = useState('');
  const [foreignAddress, setForeignAddress] = useState('');

  /**
   * Подставляем данные из DaData-подсказки.
   * Если тип в подсказке отличается от текущего — переключаем тип формы
   * на тот, что в подсказке (например, юзер выбрал ИП в SegmentedControl,
   * но в поиске указал ИНН ЮЛ → форма перестроится).
   */
  const applyLookup = (s: CompanySuggestion) => {
    if (s.type !== type) setType(s.type);

    if (s.type === 'ENTREPRENEUR') {
      setInnIp(s.inn ?? '');
      setOgrnip(s.ogrn ?? '');
      setIpRegAddress(s.legalAddress ?? '');
      // ФИО ИП — DaData отдаёт «Иванов Иван Иванович», разбиваем.
      if (s.managerName) {
        const parts = s.managerName.trim().split(/\s+/);
        if (parts.length >= 1) setLastName(parts[0]);
        if (parts.length >= 2) setFirstName(parts[1]);
        if (parts.length >= 3) setMiddleName(parts.slice(2).join(' '));
      }
      // displayName удобно сразу заполнить кратким наименованием.
    } else if (s.type === 'LEGAL_RU') {
      setLegalFullName(s.fullName);
      setLegalShortName(s.shortName ?? '');
      setLegalInn(s.inn ?? '');
      setLegalKpp(s.kpp ?? '');
      setLegalOgrn(s.ogrn ?? '');
      setLegalAddress(s.legalAddress ?? '');
      setDirectorName(s.managerName ?? '');
    } else if (s.type === 'LEGAL_FOREIGN') {
      setForeignFullName(s.fullName);
      setForeignShortName(s.shortName ?? '');
      setTaxId(s.inn ?? '');
      setForeignAddress(s.legalAddress ?? '');
    }
  };

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [devVerificationUrl, setDevVerificationUrl] = useState<string | null>(null);
  const [needAutoLogin, setNeedAutoLogin] = useState(false);
  // demo-client: логин созданного клиента (для входа в другом окне).
  const [createdClient, setCreatedClient] = useState<{ email: string; serviceId: string } | null>(
    null,
  );

  const buildProfile = (): RegisterClientRequest['profile'] => {
    switch (type) {
      case 'INDIVIDUAL':
        return {
          type,
          firstName,
          lastName,
          middleName: middleName || undefined,
          passportSerial,
          passportNumber,
          passportIssueDate,
          passportIssuedBy,
          registrationAddress: individualRegAddress,
          actualAddress: actualAddress || undefined,
        };
      case 'ENTREPRENEUR':
        return {
          type,
          firstName,
          lastName,
          middleName: middleName || undefined,
          inn: innIp,
          ogrnip,
          registrationAddress: ipRegAddress,
        };
      case 'LEGAL_RU':
        return {
          type,
          fullName: legalFullName,
          shortName: legalShortName || undefined,
          inn: legalInn,
          kpp: legalKpp,
          ogrn: legalOgrn,
          legalAddress,
          directorName,
        };
      case 'LEGAL_FOREIGN':
        return {
          type,
          fullName: foreignFullName,
          shortName: foreignShortName || undefined,
          country,
          taxId,
          address: foreignAddress,
        };
    }
  };

  /**
   * Для ЮЛ/ИП displayName в форме не показываем — он вычисляется
   * автоматически из type-specific полей. Для ФЛ юзер вводит сам.
   */
  const computedDisplayName = (): string => {
    switch (type) {
      case 'INDIVIDUAL':
        return displayName.trim();
      case 'ENTREPRENEUR': {
        const fio = [lastName, firstName, middleName].filter(Boolean).join(' ').trim();
        return fio ? `ИП ${fio}` : '';
      }
      case 'LEGAL_RU':
        return legalShortName.trim() || legalFullName.trim();
      case 'LEGAL_FOREIGN':
        return foreignShortName.trim() || foreignFullName.trim();
    }
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setDevVerificationUrl(null);
    const dto = {
      email,
      password,
      displayName: computedDisplayName(),
      phone: phone || undefined,
      profile: buildProfile(),
    };
    const parsed = registerClientSchema.safeParse(dto);
    if (!parsed.success) {
      setError(parsed.error.issues.map((i) => `${i.path.join('.')}: ${i.message}`).join('; '));
      return;
    }
    setSubmitting(true);
    try {
      if (isDemoClient) {
        // Демо-сотрудник создаёт клиента: НЕ логинимся под ним, показываем логин.
        const res = await authApi.createDemoClient(parsed.data);
        setCreatedClient({ email: res.email, serviceId: res.serviceId });
      } else {
        const res = await authApi.register(parsed.data);
        if (res.devVerification?.url) {
          setDevVerificationUrl(res.devVerification.url);
        } else {
          setNeedAutoLogin(true);
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось зарегистрироваться');
    } finally {
      setSubmitting(false);
    }
  };

  const finishLogin = async () => {
    setSubmitting(true);
    try {
      const tokens = await authApi.login({ email, password });
      setTokens(tokens);
      const me = await authApi.me();
      setUser(me);
      nav(homeRouteForRole(me.role), { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось войти после регистрации');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Container size="md" py="xl">
      <Title order={2} ta="center" mb="md">
        {isDemoClient ? 'Создание демо-клиента' : 'Регистрация клиента'}
      </Title>

      {isDemoClient && !createdClient && (
        <Alert color="orange" mb="md" title="Демо-клиент">
          <Text size="sm">
            Вы заводите клиента для демонстрации. Он будет виден только вам.
            Логин клиента активируется сразу — задайте ему email и пароль, затем
            войдите под ним в отдельном окне (режим инкогнито), чтобы увидеть
            сторону заказчика.
          </Text>
        </Alert>
      )}

      {createdClient && (
        <Alert color="green" mb="md" title="Демо-клиент создан">
          <Text size="sm">
            Клиент <strong>{createdClient.email}</strong> создан (ID сервиса{' '}
            {createdClient.serviceId}). Войдите под ним с заданным паролем в
            отдельном окне браузера или в режиме инкогнито — текущий сеанс
            экспедитора при этом сохранится.
          </Text>
          <Button mt="md" component={Link} to="/manager/clients">
            К списку клиентов
          </Button>
        </Alert>
      )}

      {devVerificationUrl && (
        <Alert color="yellow" mb="md" title="Подтвердите email">
          <Text size="sm">
            На production здесь будет email со ссылкой подтверждения. В dev — ссылка ниже:
          </Text>
          <Anchor href={devVerificationUrl} target="_blank">
            {devVerificationUrl}
          </Anchor>
          <Button mt="md" onClick={finishLogin} disabled={submitting}>
            Я подтвердил, войти
          </Button>
        </Alert>
      )}

      {needAutoLogin && !devVerificationUrl && (
        <Alert color="blue" mb="md" title="Регистрация завершена">
          Письмо со ссылкой отправлено на {email}. После подтверждения войдите{' '}
          <Anchor component={Link} to="/login">
            здесь
          </Anchor>
          .
        </Alert>
      )}

      <Paper withBorder shadow="sm" p="lg" radius="md">
        <form onSubmit={onSubmit}>
          <Stack>
            <SegmentedControl
              value={type}
              onChange={(v) => setType(clientTypeSchema.parse(v))}
              data={[
                { value: 'INDIVIDUAL', label: TYPE_LABELS.INDIVIDUAL },
                { value: 'ENTREPRENEUR', label: TYPE_LABELS.ENTREPRENEUR },
                { value: 'LEGAL_RU', label: TYPE_LABELS.LEGAL_RU },
                { value: 'LEGAL_FOREIGN', label: TYPE_LABELS.LEGAL_FOREIGN },
              ]}
              fullWidth
            />

            <TextInput
              label="Email"
              required
              value={email}
              onChange={(e) => setEmail(e.currentTarget.value)}
              type="email"
            />
            <PasswordInput
              label="Пароль"
              description="Минимум 8 символов, хотя бы одна буква и цифра"
              required
              value={password}
              onChange={(e) => setPassword(e.currentTarget.value)}
            />
            {type === 'INDIVIDUAL' && (
              <TextInput
                label="Отображаемое имя"
                required
                description="Как обращаться к вам в интерфейсе"
                value={displayName}
                onChange={(e) => setDisplayName(e.currentTarget.value)}
              />
            )}
            <TextInput
              label="Телефон"
              value={phone}
              onChange={(e) => setPhone(e.currentTarget.value)}
            />

            {/* Подсказки реквизитов (DaData) — для ИП и ЮЛ. */}
            {type !== 'INDIVIDUAL' && <CompanyLookup onSelect={applyLookup} />}

            {/* type-specific блок */}
            {(type === 'INDIVIDUAL' || type === 'ENTREPRENEUR') && (
              <>
                <Group grow>
                  <TextInput
                    label="Фамилия"
                    required
                    value={lastName}
                    onChange={(e) => setLastName(e.currentTarget.value)}
                  />
                  <TextInput
                    label="Имя"
                    required
                    value={firstName}
                    onChange={(e) => setFirstName(e.currentTarget.value)}
                  />
                </Group>
                <TextInput
                  label="Отчество"
                  value={middleName}
                  onChange={(e) => setMiddleName(e.currentTarget.value)}
                />
              </>
            )}

            {type === 'INDIVIDUAL' && (
              <>
                <Group grow>
                  <TextInput
                    label="Серия паспорта"
                    required
                    value={passportSerial}
                    onChange={(e) => setPassportSerial(e.currentTarget.value)}
                  />
                  <TextInput
                    label="Номер паспорта"
                    required
                    value={passportNumber}
                    onChange={(e) => setPassportNumber(e.currentTarget.value)}
                  />
                </Group>
                <DateField
                  label="Дата выдачи"
                  value={passportIssueDate}
                  onChange={setPassportIssueDate}
                />
                <TextInput
                  label="Кем выдан"
                  required
                  value={passportIssuedBy}
                  onChange={(e) => setPassportIssuedBy(e.currentTarget.value)}
                />
                <TextInput
                  label="Адрес регистрации"
                  required
                  value={individualRegAddress}
                  onChange={(e) => setIndividualRegAddress(e.currentTarget.value)}
                />
                <TextInput
                  label="Адрес фактический"
                  value={actualAddress}
                  onChange={(e) => setActualAddress(e.currentTarget.value)}
                />
              </>
            )}

            {type === 'ENTREPRENEUR' && (
              <>
                <Group grow>
                  <TextInput
                    label="ИНН (12 цифр)"
                    required
                    value={innIp}
                    onChange={(e) => setInnIp(e.currentTarget.value)}
                  />
                  <TextInput
                    label="ОГРНИП (15 цифр)"
                    required
                    value={ogrnip}
                    onChange={(e) => setOgrnip(e.currentTarget.value)}
                  />
                </Group>
                <TextInput
                  label="Адрес регистрации"
                  required
                  value={ipRegAddress}
                  onChange={(e) => setIpRegAddress(e.currentTarget.value)}
                />
              </>
            )}

            {type === 'LEGAL_RU' && (
              <>
                <TextInput
                  label="Полное наименование"
                  required
                  value={legalFullName}
                  onChange={(e) => setLegalFullName(e.currentTarget.value)}
                />
                <TextInput
                  label="Краткое наименование"
                  value={legalShortName}
                  onChange={(e) => setLegalShortName(e.currentTarget.value)}
                />
                <Group grow>
                  <TextInput
                    label="ИНН (10 цифр)"
                    required
                    value={legalInn}
                    onChange={(e) => setLegalInn(e.currentTarget.value)}
                  />
                  <TextInput
                    label="КПП (9 цифр)"
                    required
                    value={legalKpp}
                    onChange={(e) => setLegalKpp(e.currentTarget.value)}
                  />
                </Group>
                <TextInput
                  label="ОГРН (13 цифр)"
                  required
                  value={legalOgrn}
                  onChange={(e) => setLegalOgrn(e.currentTarget.value)}
                />
                <TextInput
                  label="Юридический адрес"
                  required
                  value={legalAddress}
                  onChange={(e) => setLegalAddress(e.currentTarget.value)}
                />
                <TextInput
                  label="ФИО руководителя"
                  required
                  value={directorName}
                  onChange={(e) => setDirectorName(e.currentTarget.value)}
                />
              </>
            )}

            {type === 'LEGAL_FOREIGN' && (
              <>
                <TextInput
                  label="Полное наименование"
                  required
                  value={foreignFullName}
                  onChange={(e) => setForeignFullName(e.currentTarget.value)}
                />
                <TextInput
                  label="Краткое наименование"
                  value={foreignShortName}
                  onChange={(e) => setForeignShortName(e.currentTarget.value)}
                />
                <Group grow>
                  <TextInput
                    label="Страна"
                    required
                    value={country}
                    onChange={(e) => setCountry(e.currentTarget.value)}
                  />
                  <TextInput
                    label="Иностранный налоговый ID"
                    required
                    value={taxId}
                    onChange={(e) => setTaxId(e.currentTarget.value)}
                  />
                </Group>
                <TextInput
                  label="Адрес"
                  required
                  value={foreignAddress}
                  onChange={(e) => setForeignAddress(e.currentTarget.value)}
                />
              </>
            )}

            <Checkbox required label="Я подтверждаю, что введённые данные верны" />
            {error && (
              <Text c="red" size="sm">
                {error}
              </Text>
            )}
            <Button
              type="submit"
              loading={submitting}
              disabled={!!devVerificationUrl || !!createdClient}
            >
              {isDemoClient ? 'Создать клиента' : 'Зарегистрироваться'}
            </Button>
            {!isDemoClient && (
              <Text size="sm" ta="center">
                Уже есть аккаунт?{' '}
                <Anchor component={Link} to="/login">
                  Войти
                </Anchor>
              </Text>
            )}
          </Stack>
        </form>
      </Paper>
    </Container>
  );
}
